import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpDown, ArrowUp, ArrowDown, Search, ChevronDown, ChevronUp } from 'lucide-react';
import { useT } from '../services/i18n';
import { productsAPI, uploadsAPI } from '../services/api';
import { useBrandLogos } from '../services/brandLogos';
import { useVehicleBrandLogos } from './VehicleBrandLogo';

const SortIcon = ({ active, dir }) => (active ? (dir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="opacity-40" />);

// Sortable « Marque / Référence » table of equivalent + OEM references, filterable by brand
const COLLAPSED_ROWS = 10;

// Logos fetched automatically for equivalence brands (admin › Marques › « Logos manquants »)
let autoLogoCache = null;
let autoLogoPending = null;
const loadAutoLogos = () => {
  if (autoLogoCache) return Promise.resolve(autoLogoCache);
  if (!autoLogoPending) autoLogoPending = productsAPI.getEquivBrandLogos().then(m => { autoLogoCache = m; return m; }).catch(() => ({}));
  return autoLogoPending;
};

// Small logo (parts manufacturer first, vehicle make as fallback) or a neutral initials tile
const EquivBrandLogo = ({ brand, url, oem = false }) => {
  const [broken, setBroken] = useState(false);
  useEffect(() => { setBroken(false); }, [url]);
  if (url && !broken) {
    return <img src={url} alt={brand} title={brand} onError={() => setBroken(true)} className={`${oem ? 'h-6 w-6 rounded-full bg-white border border-gray-200 p-0.5' : 'h-5 w-9'} object-contain shrink-0 no-placeholder`} data-testid={`equiv-${oem ? 'oem' : 'brand'}-logo-${brand}`} />;
  }
  return <span className="h-5 w-9 shrink-0 rounded bg-gray-100 text-gray-500 text-[9px] font-bold flex items-center justify-center tracking-tight" aria-hidden="true" data-testid={`equiv-brand-initials-${brand}`}>{brand.replace(/[^A-Za-z0-9]/g, '').slice(0, 3).toUpperCase()}</span>;
};

const EquivalencesTable = ({ refs = [], oemRefs = [], refBrands = {}, productId = null }) => {
  const { t } = useT();
  const [expanded, setExpanded] = useState(false);
  const partLogos = useBrandLogos();
  const carLogos = useVehicleBrandLogos();
  const [autoLogos, setAutoLogos] = useState(() => autoLogoCache || {});
  useEffect(() => { loadAutoLogos().then(setAutoLogos); }, []);
  // Parts-maker logo for equivalences ; vehicle-manufacturer logo first for OEM references
  const logoOf = (b, oem = false) => { const k = b.trim().toUpperCase(); const u = oem ? (carLogos[k] || partLogos[k] || autoLogos[k]) : (partLogos[k] || carLogos[k] || autoLogos[k]); return u ? uploadsAPI.resolveUrl(u) : null; };
  useEffect(() => {
    const onHash = () => { if (window.location.hash === '#equivalences') setExpanded(true); };
    onHash();
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  const [sort, setSort] = useState({ key: 'kind', dir: 'asc' });
  const [brand, setBrand] = useState('');
  const [q, setQ] = useState('');

  const rows = useMemo(() => [
    ...oemRefs.map(r => ({ ref: r, brand: refBrands[r] || '', oem: true })),
    ...refs.map(r => ({ ref: r, brand: refBrands[r] || '', oem: false })),
  ], [refs, oemRefs, refBrands]);
  const brands = useMemo(() => [...new Set(rows.map(r => r.brand).filter(Boolean))].sort((a, b) => a.localeCompare(b)), [rows]);

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase().replace(/[\s\-._/]/g, '');
    const list = rows.filter(r => (!brand || r.brand === brand) && (!needle || `${r.brand}${r.ref}`.toLowerCase().replace(/[\s\-._/]/g, '').includes(needle)));
    const byBrandRef = (a, b) => (a.brand || '\uffff').localeCompare(b.brand || '\uffff', undefined, { numeric: true, sensitivity: 'base' }) || a.ref.localeCompare(b.ref, undefined, { numeric: true });
    const cmp = (a, b) => {
      // « Type » : équivalences d'abord, puis OEM constructeur ; marque / référence à l'intérieur de chaque groupe
      if (sort.key === 'kind') return (Number(a.oem) - Number(b.oem)) || (sort.dir === 'asc' ? byBrandRef(a, b) : -byBrandRef(a, b));
      const va = sort.key === 'brand' ? (a.brand || '\uffff') : a.ref;
      const vb = sort.key === 'brand' ? (b.brand || '\uffff') : b.ref;
      return va.localeCompare(vb, undefined, { numeric: true, sensitivity: 'base' }) || a.ref.localeCompare(b.ref, undefined, { numeric: true });
    };
    list.sort((a, b) => (sort.dir === 'asc' ? cmp(a, b) : -cmp(a, b)));
    return list;
  }, [rows, brand, q, sort]);

  // Searching or filtering reveals every match ; otherwise the table stays short until « Voir plus »
  const showAll = expanded || !!q.trim() || !!brand;
  const shown = showAll ? visible : visible.slice(0, COLLAPSED_ROWS);
  const hidden = visible.length - shown.length;

  if (!rows.length) return null;
  const toggle = (key) => setSort(s => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  const Th = ({ k, label, className = '' }) => (
    <th className={`py-2 px-3 text-left font-semibold text-gray-600 ${className}`}>
      <button type="button" onClick={() => toggle(k)} data-testid={`equiv-sort-${k}`} className="inline-flex items-center gap-1 hover:text-gray-900">{label} <SortIcon active={sort.key === k} dir={sort.dir} /></button>
    </th>
  );

  return (
    <section id="equivalences" className="bg-white rounded-xl shadow-sm p-6 scroll-mt-24" data-testid="product-equivalences">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-xl font-bold text-gray-900">{t('equiv.title')} <span className="text-sm font-normal text-gray-500" data-testid="equiv-count">({t('equiv.count', { n: visible.length, count: visible.length })})</span></h2>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('equiv.search')} data-testid="equiv-search" className="pl-7 pr-2 py-1.5 text-sm border border-gray-200 rounded-md w-40 font-mono focus:outline-none focus:ring-2 focus:ring-orange-300" />
          </div>
          {brands.length > 1 && (
            <select value={brand} onChange={e => setBrand(e.target.value)} data-testid="equiv-brand-filter" className="text-sm border border-gray-200 rounded-md py-1.5 px-2 bg-white focus:outline-none focus:ring-2 focus:ring-orange-300">
              <option value="">{t('equiv.allBrands', { n: brands.length })}</option>
              {brands.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          )}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="equiv-table">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <Th k="brand" label={t('equiv.brand')} className="w-1/2" />
              <Th k="ref" label={t('equiv.ref')} />
              <Th k="kind" label={t('equiv.type')} className="w-44 whitespace-nowrap" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {shown.map(r => (
              <tr key={`${r.oem}-${r.ref}`} data-testid={`equiv-row-${r.ref}`} className="hover:bg-orange-50/40">
                <td className="py-2 px-3 font-medium text-gray-800 uppercase text-xs tracking-wide" data-testid={`equiv-brand-${r.ref}`}>
                  {r.brand
                    ? <span className="inline-flex flex-wrap items-center gap-2">{r.brand.split(' / ').map((b, i) => (
                      <React.Fragment key={b}>{i > 0 && <span className="text-gray-400">/</span>}<Link to={`/recherche?equiv=${encodeURIComponent(b)}`} rel="nofollow" onClick={() => productsAPI.trackEquiv(b, r.ref, productId)} className="inline-flex items-center gap-2 hover:text-orange-600 hover:underline" title={t('equiv.brandLink', { b })} data-testid={`equiv-brand-link-${r.ref}-${i}`}><EquivBrandLogo brand={b} url={logoOf(b, r.oem)} oem={r.oem} />{b}</Link></React.Fragment>
                    ))}</span>
                    : <span className="text-gray-400 normal-case">{t('equiv.unknownBrand')}</span>}
                </td>
                <td className="py-2 px-3 font-mono text-gray-900">{r.ref}</td>
                <td className="py-2 px-3">
                  <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${r.oem ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'bg-gray-100 text-gray-600'}`}>{r.oem ? t('equiv.oem') : t('equiv.alt')}</span>
                </td>
              </tr>
            ))}
            {!visible.length && <tr><td colSpan={3} className="py-6 text-center text-gray-500" data-testid="equiv-empty">{t('equiv.empty')}</td></tr>}
          </tbody>
        </table>
      </div>
      {(hidden > 0 || (expanded && visible.length > COLLAPSED_ROWS)) && (
        <button type="button" onClick={() => { setExpanded(e => !e); if (expanded) document.getElementById('equivalences')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }} data-testid="equiv-toggle-more"
          className="mt-3 w-full inline-flex items-center justify-center gap-1.5 text-sm font-semibold text-[#1e3a5f] bg-gray-50 hover:bg-orange-50 border border-gray-200 hover:border-orange-200 rounded-lg py-2 transition-colors">
          {expanded ? <><ChevronUp size={16} /> {t('equiv.showLess')}</> : <><ChevronDown size={16} /> {t('equiv.showMore', { n: hidden })}</>}
        </button>
      )}
    </section>
  );
};

export default EquivalencesTable;
